/**
 * Duration, position, buffering and seekability — the four numbers the UI needs and the
 * four the media element is worst at reporting (spec.md §13.5).
 *
 * Everything here is a pure read over a {@link MediaElementLike}. No listeners, no state,
 * no timers: `events.ts` decides *when* to read, this module decides *what the answer is*.
 *
 * The element's own numbers cannot be used raw:
 *
 * - `duration` is `NaN` before `loadedmetadata`, `Infinity` for an unbounded resource, and
 *   for a VBR MP3 with no Xing/VBRI header it keeps **changing** through `durationchange`
 *   as the file is read. So the server's duration (which the indexer took from
 *   `DocumentAttributeAudio.duration`) is rendered immediately and the element's value
 *   takes over only once it is finite and positive. The UI therefore never flickers
 *   `--:--` or `NaN`.
 * - `currentTime` can round a hair **past** `duration` at track end, which is enough to
 *   make `MediaSession.setPositionState` throw.
 * - `buffered`/`seekable` are live `TimeRanges`; indexing one out of range throws, and some
 *   engines throw on `start(0)` while `length === 0`. Every access here is guarded.
 *
 * A note on `isFullySeekable`: it is a probe of **our own server's** range support from the
 * client side. If a Range request path is broken or a proxy strips `Accept-Ranges`, the
 * element reports a seekable window that stops at whatever it has downloaded. Presenting a
 * full-width scrub bar in that state is a lie, so the UI degrades to "no dragging past
 * buffered" instead.
 */

import { audio, type MediaElementLike, type TimeRangesLike } from './element';

/** How far short of `duration` the seekable window may stop and still count as full. */
export const SEEKABLE_TOLERANCE_S = 0.5;

/* ══ scalar guards ═════════════════════════════════════════════════════════════════ */

/** A finite, non-negative number, or `fallback`. Absorbs `NaN`, `Infinity` and `-0`. */
function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

/** Reads a `TimeRanges` accessor that is allowed to throw, yielding `null` on failure. */
function readRange(
  ranges: TimeRangesLike | null | undefined,
  index: number,
  edge: 'start' | 'end',
): number | null {
  if (ranges === null || ranges === undefined) return null;
  try {
    if (index < 0 || index >= ranges.length) return null;
    const value = edge === 'start' ? ranges.start(index) : ranges.end(index);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/** `ranges.length`, or 0 when the accessor is missing or throws. */
function rangeCount(ranges: TimeRangesLike | null | undefined): number {
  if (ranges === null || ranges === undefined) return 0;
  try {
    const { length } = ranges;
    return Number.isFinite(length) && length > 0 ? Math.floor(length) : 0;
  } catch {
    return 0;
  }
}

/* ══ the four public readings ══════════════════════════════════════════════════════ */

/**
 * The duration to render, in seconds (spec.md §13.5).
 *
 * The element wins as soon as it knows something finite and positive; until then the
 * server's value carries the UI. Returns `0` when neither source is usable, which callers
 * must treat as "unknown" — never as "zero-length".
 */
export function effectiveDuration(serverDurationS: number, el: MediaElementLike = audio): number {
  const fromElement = el.duration;
  if (Number.isFinite(fromElement) && fromElement > 0) return fromElement;
  return Number.isFinite(serverDurationS) && serverDurationS > 0 ? serverDurationS : 0;
}

/** `true` once the element itself has a usable duration (i.e. `loadedmetadata` landed). */
export function hasElementDuration(el: MediaElementLike = audio): boolean {
  const d = el.duration;
  return Number.isFinite(d) && d > 0;
}

/** `currentTime`, scrubbed of `NaN`/`Infinity` and clamped into `[0, duration]`. */
export function currentPosition(serverDurationS = 0, el: MediaElementLike = audio): number {
  const position = finiteOr(el.currentTime, 0);
  const duration = effectiveDuration(serverDurationS, el);
  if (duration <= 0) return position;
  return Math.min(position, duration);
}

/**
 * Played fraction in `[0, 1]`, or `0` while the duration is unknown.
 *
 * Never `NaN`: a `NaN` here propagates into a CSS custom property and silently collapses
 * the playhead to the left edge for the rest of the session.
 */
export function progressFraction(serverDurationS: number, el: MediaElementLike = audio): number {
  const duration = effectiveDuration(serverDurationS, el);
  if (duration <= 0) return 0;
  const fraction = currentPosition(serverDurationS, el) / duration;
  if (!Number.isFinite(fraction)) return 0;
  return Math.min(1, Math.max(0, fraction));
}

/**
 * The end of the buffered range that contains (or immediately follows) `currentTime`, in
 * seconds; `null` when nothing usable is buffered.
 */
export function bufferedEnd(el: MediaElementLike = audio): number | null {
  const ranges = el.buffered;
  const count = rangeCount(ranges);
  const position = finiteOr(el.currentTime, 0);
  for (let index = 0; index < count; index += 1) {
    const start = readRange(ranges, index, 'start');
    const end = readRange(ranges, index, 'end');
    if (start === null || end === null) continue;
    // A tiny tolerance on the low side: `currentTime` routinely sits a few
    // microseconds before the range that actually holds it.
    if (position >= start - 0.05 && position <= end) return end;
  }
  return null;
}

/**
 * Seconds of contiguous audio buffered ahead of the playhead. `0` when the playhead sits
 * in a gap — which is exactly the state that produces a `waiting` event.
 */
export function bufferedAhead(el: MediaElementLike = audio): number {
  const end = bufferedEnd(el);
  if (end === null) return 0;
  const ahead = end - finiteOr(el.currentTime, 0);
  return ahead > 0 ? ahead : 0;
}

/**
 * `true` when the element reports a seekable window that reaches the end of the track —
 * i.e. when the server's Range support is working and the whole track can be scrubbed
 * (spec.md §13.5).
 *
 * `false` while the duration is unknown or no seekable range exists yet: an unknown answer
 * must degrade the UI, not promise full seeking.
 */
export function isFullySeekable(
  serverDurationS: number,
  el: MediaElementLike = audio,
  toleranceS: number = SEEKABLE_TOLERANCE_S,
): boolean {
  const duration = effectiveDuration(serverDurationS, el);
  if (duration <= 0) return false;
  const ranges = el.seekable;
  const count = rangeCount(ranges);
  if (count === 0) return false;
  const end = readRange(ranges, count - 1, 'end');
  if (end === null) return false;
  return end >= duration - toleranceS;
}
